import { useState, useEffect } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

// Lista de ciudades españolas principales
const SPANISH_CITIES = [
  "Madrid",
  "Barcelona", 
  "Valencia",
  "Sevilla",
  "Zaragoza",
  "Málaga",
  "Murcia",
  "Palma",
  "Las Palmas de Gran Canaria",
  "Bilbao",
  "Alicante",
  "Córdoba",
  "Valladolid",
  "Vigo",
  "Gijón",
  "Hospitalet de Llobregat",
  "Granada",
  "Vitoria-Gasteiz",
  "Elche",
  "Santa Cruz de Tenerife",
  "Oviedo",
  "Badalona",
  "Cartagena",
  "Terrassa",
  "Jerez de la Frontera",
  "Sabadell",
  "Móstoles",
  "Alcalá de Henares",
  "Pamplona",
  "Fuenlabrada",
  "Almería",
  "San Sebastián",
  "Leganés",
  "Getafe",
  "Santander",
  "Burgos",
  "Castellón de la Plana",
  "Alcorcón",
  "Albacete",
  "Logroño",
  "Badajoz",
  "Salamanca",
  "Huelva",
  "Marbella",
  "Lleida",
  "Tarragona",
  "León",
  "Cádiz",
  "Dos Hermanas",
  "Jaén",
  "Ourense",
  "Parla",
  "Torrejón de Ardoz",
  "Alcobendas",
  "Reus",
  "Telde",
  "Barakaldo",
  "Lugo",
  "San Fernando",
  "Girona",
  "Santiago de Compostela",
  "Cáceres",
  "Lorca",
  "Coslada",
  "Talavera de la Reina",
  "El Puerto de Santa María",
  "Cornellà de Llobregat",
  "Avilés",
  "Palencia",
  "Galdakao",
  "Pontevedra",
  "Pozuelo de Alarcón",
  "Toledo",
  "Guadalajara",
  "Torrent",
  "Manresa",
  "Ciudad Real",
  "Rubí",
  "Benidorm",
  "Alzira",
  "San Cugat del Vallés",
  "Zamora",
  "Sagunto",
  "Ávila",
  "Mérida",
  "Arrecife",
  "Cuenca",
  "Collado Villalba",
  "Segovia",
  "Orihuela",
  "Chiclana de la Frontera",
  "Manacor",
  "Ferrol",
  "Vélez-Málaga",
  "Paterna",
  "Ibiza",
  "Puertollano",
  "Vilanova i la Geltrú",
  "Gandía",
  "Linares",
  "Alcalá de Guadaíra",
  "Ceuta",
  "Melilla"
];

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const LocationAutocomplete = ({ value, onChange, placeholder = "Buscar ciudad..." }: LocationAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(value);

  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  const filteredCities = SPANISH_CITIES.filter(city =>
    city.toLowerCase().includes(searchValue.toLowerCase())
  ).slice(0, 10); // Mostrar máximo 10 resultados

  const handleSelect = (selectedCity: string) => {
    onChange(selectedCity);
    setSearchValue(selectedCity);
    setOpen(false);
  };

  const handleInputChange = (newValue: string) => {
    setSearchValue(newValue);
    onChange(newValue);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-left font-normal"
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value || placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Buscar ciudad..." 
            value={searchValue}
            onValueChange={handleInputChange}
          />
          <CommandList>
            <CommandEmpty>
              {searchValue.length > 0 ? "No se encontraron ciudades" : "Escribe para buscar ciudades"}
            </CommandEmpty>
            {filteredCities.length > 0 && (
              <CommandGroup>
                {filteredCities.map((city) => (
                  <CommandItem
                    key={city}
                    value={city}
                    onSelect={() => handleSelect(city)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === city ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                    {city}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {searchValue.length > 0 && !filteredCities.includes(searchValue) && (
              <CommandGroup>
                <CommandItem
                  value={searchValue}
                  onSelect={() => handleSelect(searchValue)}
                  className="cursor-pointer"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                  {searchValue} (personalizado)
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};